'use client';

import React, { useEffect } from 'react';
import { CircleExclamationIcon, RotateRightIcon } from '@/components/ui/Icons';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const isOfflinePage = typeof window !== 'undefined' && 
    (window.location.pathname === '/offline' || 
     window.location.pathname === '/offline/' || 
     window.location.pathname.endsWith('/offline.html'));

  useEffect(() => {
    console.error('Unhandled app error:', error);
    // If the browser is offline when an error occurs and we are not already on the offline page, redirect directly to the offline scanner page
    if (typeof window !== 'undefined' && !navigator.onLine && !isOfflinePage) {
      try {
        sessionStorage.setItem('offline_fallback_prev_path', window.location.pathname + window.location.search);
      } catch (e) {
        console.error('Failed to save offline prev path:', e);
      }
      window.location.href = '/offline';
    }
  }, [error, isOfflinePage]);

  const handleReset = () => {
    // If the browser is offline on retry and we are not already on the offline page, redirect to the offline scanner page instead of reloading
    if (typeof window !== 'undefined' && !navigator.onLine && !isOfflinePage) {
      try {
        sessionStorage.setItem('offline_fallback_prev_path', window.location.pathname + window.location.search);
      } catch (e) {
        console.error('Failed to save offline prev path on reset:', e);
      }
      window.location.href = '/offline';
      return;
    }

    try {
      reset();
    } catch (e) {
      console.error('Error boundary reset failed:', e);
    }
    // Force a reload to clean the page state and retry network fetching
    window.location.reload();
  };

  return (
    <div className="pt-20 md:pt-28 pb-12 flex items-center justify-center px-4">
      <main className="text-center z-10 max-w-md w-full p-6 sm:p-8 rounded-3xl bg-[var(--card-bg)] backdrop-blur-xl border border-[var(--card-border)] shadow-2xl">
        <div className="h-20 w-20 mx-auto mb-6 rounded-3xl bg-red-500/10 flex items-center justify-center border border-red-500/30">
          <CircleExclamationIcon className="text-4xl text-red-500" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">Something Went Wrong!</h1>
        <p className="text-sm sm:text-base text-slate-400 mb-8 leading-relaxed">
          An unexpected error occurred while rendering this page. You can try refreshing the section using the button below.
        </p>

        <div className="error-actions-container flex flex-col sm:flex-row justify-center gap-4">
          <button
            onClick={handleReset}
            className="cta-button inline-flex items-center justify-center text-white font-bold py-3 px-6 rounded-full text-base transition-all duration-300 hover:scale-105 w-full sm:w-auto cursor-pointer"
          >
            <RotateRightIcon className="mr-2 shrink-0 animate-spin-slow" /> <span className="whitespace-nowrap">Try Again</span>
          </button>
          <a
            href="/"
            className="font-semibold text-slate-300 border-2 border-slate-700 rounded-full py-3 px-6 text-base transition-all duration-300 hover:bg-white/5 hover:border-slate-500 w-full sm:w-auto inline-flex items-center justify-center whitespace-nowrap"
          >
            Go to Home
          </a>
        </div>
      </main>
    </div>
  );
}
